import { useCallback, useEffect, useRef, useState } from 'react'

import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { twMerge } from 'tailwind-merge'
import { v4 as uuid } from 'uuid'

import { Size } from '../../types'
import HStack from '../Flex/HStack'
import FormControl from '../FormControl/FormControl'
import { FormControlProps } from '../FormControl/util'
import Icon from '../Icon'
import { T } from '../Text/Text'

type CheckboxBaseProps = Omit<FormControlProps, 'ariaLabel'> & {
  isDisabled?: boolean
  onValueChange?: (value: true | false | 'indeterminate') => void
  size?: Size
  tabIndex?: number
  value?: true | false | 'indeterminate'
} & (
    | {
        ariaLabel: string
        children?: never
      }
    | {
        ariaLabel?: never
        children: React.ReactNode
      }
  )
const Checkbox = ({
  ariaLabel,
  caption,
  children,
  errorMessage,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  onValueChange,
  size = 'md',
  tabIndex,
  tooltip,
  value = false,
}: CheckboxBaseProps) => {
  const [id] = useState(uuid())
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    /**
     * The Radix Checkbox implementation is a button with a hidden associated input element.
     * They do not expose the input, so this is somewhat fragile, but necessary for accessibility purposes.
     * The id needs to be set on the input to be referenced by the associated labels for screen readers.
     * https://github.com/radix-ui/primitives/discussions/874
     */
    if (!containerRef.current) {
      return
    }
    const input = containerRef.current.querySelector('input')
    if (!input) {
      return
    }

    input.id = id
    if (ariaLabel) {
      input.ariaLabel = ariaLabel
    }
  }, [ariaLabel, id])

  const handleCheckedChange = useCallback(
    (newValue: true | false | 'indeterminate') => {
      if (isDisabled) {
        return
      }
      onValueChange?.(newValue)
    },
    [isDisabled, onValueChange]
  )

  const baseClasses =
    'flex items-center justify-center w-4 min-w-4 h-4 min-h-4 p-0 border border-solid rounded border-neutral-300 bg-white outline-outline outline-offset-1'
  const hoverClasses = 'hover:border-orange-500 group-hover/table-row:border-orange-500'
  const focusClasses = 'focus-visible:outline'
  const checkedClasses =
    'data-[state=checked]:border-orange-500 data-[state=checked]:bg-orange-500 data-[state=checked]:text-white data-[state=checked]:[&_svg]:text-white'
  const disabledClasses =
    'data-[disabled]:cursor-not-allowed data-[disabled]:bg-neutral-100 data-[disabled]:border-neutral-300 data-[disabled]:hover:border-neutral-300'

  const checkboxClasses = twMerge(
    baseClasses,
    hoverClasses,
    focusClasses,
    checkedClasses,
    disabledClasses
  )

  return (
    <FormControl
      caption={caption}
      errorMessage={errorMessage}
      id={id}
      isInvalid={isInvalid}
      isLabelRedundant
      isRequired={isRequired}
      label={label}
      tooltip={tooltip}
    >
      {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
        <HStack ref={containerRef}>
          <CheckboxPrimitive.Root
            className={checkboxClasses}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-live={ariaLive}
            aria-label={ariaLabel || 'Toggle checked'}
            checked={value}
            disabled={isDisabled}
            id={id}
            onCheckedChange={handleCheckedChange}
            tabIndex={tabIndex}
            {...(tabIndex === -1
              ? {
                  role: 'presentation',
                  ['aria-checked']: false,
                }
              : {})}
          >
            <CheckboxPrimitive.Indicator>
              <T.Body>
                <Icon icon={value === 'indeterminate' ? 'FaHyphen' : 'FaCheck'} />
              </T.Body>
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>
          {children ? (
            <label
              className={`pl-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              htmlFor={id}
            >
              {size === 'md' ? <T.Body>{children}</T.Body> : <T.BodySmall>{children}</T.BodySmall>}
            </label>
          ) : null}
        </HStack>
      )}
    </FormControl>
  )
}

export default Checkbox
